import React, { useState, useEffect } from "react"
import { motion } from "framer-motion"
import {Save, Printer, FileDown, Home} from 'lucide-react'
import toast from "react-hot-toast"
import { useGuias } from "../context/GuiasContext"

interface AforamentoProps {
  editingAforamentoId?: string
  onSaveComplete?: () => void
}

export default function Aforamento({ editingAforamentoId, onSaveComplete }: AforamentoProps) {
  const { addAforamento, updateAforamento, getAforamentoById, logoUrl, dadosPrefeitura } = useGuias()
  const [formData, setFormData] = useState({
    numero: "",
    proprietario: "",
    cpfProprietario: "",
    denominacao: "",
    regiao: "",
    valorVenal: "",
    ano: "",
    impostoAPagar: "",
    numeroTalao: "",
    dataPagamento: "",
    observacao: "",
  })

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, "")
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")
    }
    return value
  }

  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value)
    setFormData(prev => ({ ...prev, cpfProprietario: formatted }))
  }

  const formatValor = (value: string) => {
    let numero = value.replace(/\D/g, "")
    if (!numero) return "R$ 0,00"
    const valorNumerico = parseInt(numero) / 100
    return "R$ " + valorNumerico.toFixed(2).replace(".", ",")
  }

  const handleValorChange = (e: React.ChangeEvent<HTMLInputElement>, field: string) => {
    const formatted = formatValor(e.target.value)
    setFormData(prev => ({ ...prev, [field]: formatted }))
  }

  const handleValorBlur = (field: string) => {
    const valor = formData[field as keyof typeof formData]
    if (typeof valor === "string" && valor) {
      const apenasNumeros = valor.replace(/\D/g, "")
      if (!apenasNumeros) {
        setFormData(prev => ({ ...prev, [field]: "R$ 0,00" }))
      } else if (!valor.startsWith("R$")) {
        const valorFormatado = formatValor(apenasNumeros)
        setFormData(prev => ({ ...prev, [field]: valorFormatado }))
      }
    }
  }

  // Carregar dados do aforamento se estiver editando
  useEffect(() => {
    if (editingAforamentoId) {
      const aforamento = getAforamentoById(editingAforamentoId)
      if (aforamento) {
        setFormData({
          numero: aforamento.numero,
          proprietario: aforamento.proprietario,
          cpfProprietario: aforamento.cpfProprietario,
          denominacao: aforamento.denominacao,
          regiao: aforamento.regiao,
          valorVenal: aforamento.valorVenal,
          ano: aforamento.ano,
          impostoAPagar: aforamento.impostoAPagar,
          numeroTalao: aforamento.numeroTalao,
          dataPagamento: aforamento.dataPagamento,
          observacao: aforamento.observacao,
        })
      }
    }
  }, [editingAforamentoId, getAforamentoById])

  const handleSave = async () => {
    // Validação completa de todos os campos obrigatórios
    if (!formData.numero || !formData.proprietario || !formData.cpfProprietario || 
        !formData.denominacao || !formData.regiao || !formData.valorVenal || 
        !formData.ano || !formData.impostoAPagar || !formData.numeroTalao || 
        !formData.dataPagamento) {
      toast.error("Preencha todos os campos obrigatórios marcados com *")
      return
    }

    if (editingAforamentoId) {
      await updateAforamento(editingAforamentoId, formData)
    } else {
      await addAforamento(formData)
      // Limpar formulário
      setFormData({
        numero: "",
        proprietario: "",
        cpfProprietario: "",
        denominacao: "",
        regiao: "",
        valorVenal: "",
        ano: "",
        impostoAPagar: "",
        numeroTalao: "",
        dataPagamento: "",
        observacao: "",
      })
    }
    
    if (onSaveComplete) {
      onSaveComplete()
    }
  }

  const handlePrint = () => {
    const printWindow = window.open("", "_blank")
    if (!printWindow) {
      toast.error("Não foi possível abrir a janela de impressão")
      return
    }

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Documento de Aforamento - ${formData.numero}</title>
          <style>
            body { 
              margin: 0; 
              padding: 40px; 
              font-family: Arial, sans-serif;
            }
            @media print {
              body { margin: 0; padding: 15mm; }
              @page { size: A4; margin: 10mm; }
            }
            .header {
              display: flex;
              gap: 24px;
              margin-bottom: 24px;
              padding-bottom: 24px;
              border-bottom: 2px solid #d1d5db;
            }
            .header img {
              height: 80px;
              width: auto;
              object-fit: contain;
            }
            .header-info {
              flex: 1;
            }
            .header-info h2 {
              font-size: 20px;
              font-weight: bold;
              color: #1f2937;
              margin: 0 0 8px 0;
            }
            .header-info p {
              font-size: 12px;
              color: #4b5563;
              margin: 2px 0;
            }
            .title {
              font-size: 18px;
              font-weight: bold;
              text-align: center;
              color: #1f2937;
              margin-bottom: 20px;
              text-transform: uppercase;
            }
            .section {
              margin-bottom: 16px;
            }
            .section h4 {
              font-size: 14px;
              font-weight: 600;
              color: #374151;
              margin-bottom: 12px;
              padding-bottom: 8px;
              border-bottom: 1px solid #d1d5db;
            }
            .grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 12px;
            }
            .field {
              margin-bottom: 8px;
            }
            .field-label {
              font-size: 11px;
              color: #6b7280;
              margin-bottom: 2px;
            }
            .field-value {
              font-size: 12px;
              font-weight: 500;
              color: #1f2937;
            }
            .full-width {
              grid-column: 1 / -1;
            }
            .signatures {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 40px;
              margin-top: 40px;
            }
            .signature-box {
              text-align: center;
              border-top: 2px solid #9ca3af;
              padding-top: 8px;
            }
            .signature-box p {
              font-size: 11px;
              margin: 4px 0;
            }
            .signature-title {
              font-weight: 500;
              color: #374151;
            }
            .signature-subtitle {
              color: #6b7280;
            }
            .footer {
              margin-top: 32px;
              text-align: center;
              font-size: 10px;
              color: #6b7280;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="${logoUrl}" alt="Logo do Município" />
            <div class="header-info">
              <h2>${dadosPrefeitura?.nomePrefeitura || "Prefeitura Municipal"}</h2>
              <p>${dadosPrefeitura?.secretaria || ""}</p>
              <p>${dadosPrefeitura?.departamento || ""}</p>
              <p>${dadosPrefeitura?.endereco || ""} | CEP: ${dadosPrefeitura?.cep || ""}</p>
              <p>CNPJ: ${dadosPrefeitura?.cnpj || ""} | Tel: ${dadosPrefeitura?.telefone || ""}</p>
            </div>
          </div>
          
          <h3 class="title">DOCUMENTO DE AFORAMENTO Nº ${formData.numero}</h3>
          
          <div class="section">
            <h4>Informações do Proprietário</h4>
            <div class="grid">
              <div class="field">
                <p class="field-label">Nome do Proprietário:</p>
                <p class="field-value">${formData.proprietario}</p>
              </div>
              <div class="field">
                <p class="field-label">CPF:</p>
                <p class="field-value">${formData.cpfProprietario}</p>
              </div>
            </div>
          </div>
          
          <div class="section">
            <h4>Informações da Propriedade</h4>
            <div class="grid">
              <div class="field full-width">
                <p class="field-label">Denominação da Propriedade:</p>
                <p class="field-value">${formData.denominacao}</p>
              </div>
              <div class="field">
                <p class="field-label">Região:</p>
                <p class="field-value">${formData.regiao}</p>
              </div>
              <div class="field">
                <p class="field-label">Valor Venal:</p>
                <p class="field-value">R$ ${formData.valorVenal}</p>
              </div>
            </div>
          </div>
          
          <div class="section">
            <h4>Informações do Pagamento</h4>
            <div class="grid">
              <div class="field">
                <p class="field-label">Ano:</p>
                <p class="field-value">${formData.ano}</p>
              </div>
              <div class="field">
                <p class="field-label">Imposto a Pagar:</p>
                <p class="field-value">R$ ${formData.impostoAPagar}</p>
              </div>
              <div class="field">
                <p class="field-label">Nº do Talão:</p>
                <p class="field-value">${formData.numeroTalao}</p>
              </div>
              <div class="field">
                <p class="field-label">Data do Pagamento:</p>
                <p class="field-value">${formData.dataPagamento ? new Date(formData.dataPagamento).toLocaleDateString("pt-BR") : ""}</p>
              </div>
              ${formData.observacao ? `
              <div class="field full-width">
                <p class="field-label">Observação:</p>
                <p class="field-value">${formData.observacao}</p>
              </div>
              ` : ""}
            </div>
          </div>
          
          <div class="signatures">
            <div class="signature-box">
              <p class="signature-title">Assinatura do Funcionário</p>
              <p class="signature-subtitle">${dadosPrefeitura?.secretaria || "Secretaria Municipal"}</p>
            </div>
            <div class="signature-box">
              <p class="signature-title">Assinatura do Requerente</p>
              <p class="signature-subtitle">${formData.proprietario || "Nome do Proprietário"}</p>
            </div>
          </div>
          
          <div class="footer">
            <p>Documento emitido em ${new Date().toLocaleDateString("pt-BR")} às ${new Date().toLocaleTimeString("pt-BR")}</p>
          </div>
          
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print()
              }, 500)
            }
          </script>
        </body>
      </html>
    `

    printWindow.document.write(htmlContent)
    printWindow.document.close()
    toast.success("Documento enviado para impressão")
  }

  const handleExportPDF = () => {
    handlePrint()
    toast.success("Use 'Salvar como PDF' na janela de impressão")
  }

  return (
    <div className="space-y-6">
      {/* Header com Logo e Informações Institucionais */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-8 border border-gray-200 print-guia"
      >
        <div className="flex items-start gap-4 mb-4 pb-4 border-b border-gray-200">
          <img
            src={logoUrl}
            alt="Logo do Município"
            className="h-20 w-auto object-contain"
          />
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-800 mb-1">
              {dadosPrefeitura?.nomePrefeitura || "Prefeitura Municipal"}
            </h2>
            <p className="text-xs text-gray-600">{dadosPrefeitura?.secretaria}</p>
            <p className="text-xs text-gray-600">{dadosPrefeitura?.departamento}</p>
            <p className="text-xs text-gray-600 mt-1">
              {dadosPrefeitura?.endereco} | CEP: {dadosPrefeitura?.cep}
            </p>
            <p className="text-xs text-gray-600">
              CNPJ: {dadosPrefeitura?.cnpj} | Tel: {dadosPrefeitura?.telefone}
            </p>
          </div>
        </div>

        <h3 className="text-lg font-bold text-center text-gray-800 mb-4">
          DOCUMENTO DE AFORAMENTO
        </h3>

        {/* Número do Aforamento */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Número do Aforamento *
          </label>
          <input
            type="text"
            name="numero"
            value={formData.numero}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            placeholder="Ex: AF001/2024"
            required
          />
        </div>

        {/* Informações do Proprietário */}
        <div className="mb-8">
          <h4 className="text-lg font-semibold text-gray-700 mb-4 pb-2 border-b border-gray-300">
            Informações do Proprietário
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nome do Proprietário *
              </label>
              <input
                type="text"
                name="proprietario"
                value={formData.proprietario}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Nome completo"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CPF do Proprietário *
              </label>
              <input
                type="text"
                name="cpfProprietario"
                value={formData.cpfProprietario}
                onChange={handleCPFChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="000.000.000-00"
                maxLength={14}
                required
              />
            </div>
          </div>
        </div>

        {/* Informações da Propriedade */}
        <div className="mb-8">
          <h4 className="text-lg font-semibold text-gray-700 mb-4 pb-2 border-b border-gray-300">
            Informações da Propriedade
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Denominação da Propriedade *
              </label>
              <input
                type="text"
                name="denominacao"
                value={formData.denominacao}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Nome ou identificação da propriedade"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Região *
              </label>
              <input
                type="text"
                name="regiao"
                value={formData.regiao}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Localização/região"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Valor Venal (R$) *
              </label>
              <input
                type="text"
                name="valorVenal"
                value={formData.valorVenal}
                onChange={(e) => handleValorChange(e, "valorVenal")}
                onBlur={() => handleValorBlur("valorVenal")}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="0,00"
                required
              />
            </div>
          </div>
        </div>

        {/* Informações do Pagamento */}
        <div className="mb-8">
          <h4 className="text-lg font-semibold text-gray-700 mb-4 pb-2 border-b border-gray-300">
            Informações do Pagamento
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ano *
              </label>
              <input
                type="text"
                name="ano"
                value={formData.ano}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="2024"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Imposto a Pagar (R$) *
              </label>
              <input
                type="text"
                name="impostoAPagar"
                value={formData.impostoAPagar}
                onChange={(e) => handleValorChange(e, "impostoAPagar")}
                onBlur={() => handleValorBlur("impostoAPagar")}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="0,00"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nº do Talão *
              </label>
              <input
                type="text"
                name="numeroTalao"
                value={formData.numeroTalao}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Número do talão"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data do Pagamento *
              </label>
              <input
                type="date"
                name="dataPagamento"
                value={formData.dataPagamento}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observação
              </label>
              <textarea
                name="observacao"
                value={formData.observacao}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Observações adicionais (opcional)"
              />
            </div>
          </div>
        </div>

        {/* Assinaturas */}
        <div className="mb-8">
          <h4 className="text-lg font-semibold text-gray-700 mb-4 pb-2 border-b border-gray-300">
            Assinaturas
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="border-t-2 border-gray-400 pt-2 text-center">
              <p className="text-sm font-medium text-gray-700">Assinatura do Funcionário</p>
              <p className="text-xs text-gray-500 mt-1">{dadosPrefeitura?.secretaria}</p>
            </div>
            <div className="border-t-2 border-gray-400 pt-2 text-center">
              <p className="text-sm font-medium text-gray-700">Assinatura do Requerente</p>
              <p className="text-xs text-gray-500 mt-1">{formData.proprietario || "Nome do Proprietário"}</p>
            </div>
          </div>
        </div>

        {/* Botões de Ação */}
        <div className="flex flex-wrap gap-4 justify-end pt-6 border-t border-gray-200 no-print">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleSave}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-lg"
          >
            <Save className="w-5 h-5" />
            Salvar Aforamento
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleExportPDF}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-lg"
          >
            <FileDown className="w-5 h-5" />
            Salvar em PDF
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handlePrint}
            className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-lg"
          >
            <Printer className="w-5 h-5" />
            Imprimir
          </motion.button>
        </div>
      </motion.div>
    </div>
  )
}
